import React, { useState } from 'react';

export interface RelationChip {
  /** Stable key, also what gets handed to onSelect. */
  key: string;
  label: string;
  /** Secondary line shown in the chip title, e.g. a hotel code or a connector id. */
  hint?: string | null;
  /** Integration / connector status, used only to tint the chip. */
  status?: string | null;
}

interface RelationChipsProps {
  chips: RelationChip[];
  /** Called with the chip key when a chip is clicked. Omit to render static chips. */
  onSelect?: (chip: RelationChip) => void;
  /** How many chips to show before collapsing the rest behind a "+N" toggle. */
  maxVisible?: number;
  /** Shown when there is nothing linked - the gap is the point, so it stays visible. */
  emptyLabel?: string;
  /**
   * `warning` when the relations could not be loaded: "nothing linked" and "we do not
   * know" must not look the same, or the screen quietly asserts something false.
   */
  emptyVariant?: 'muted' | 'warning';
}

function chipTone(status?: string | null): string {
  switch (status) {
    case 'active':
      return 'bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100';
    case 'pending':
      return 'bg-yellow-50 text-yellow-800 border-yellow-200 hover:bg-yellow-100';
    case 'inactive':
      return 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100';
    default:
      return 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100';
  }
}

/**
 * Renders the objects a row is linked to as clickable chips.
 *
 * Used by the hotel, connector and SaaS listings so each one shows - and can jump to -
 * the other two sides of the integration graph.
 */
export function RelationChips({
  chips,
  onSelect,
  maxVisible = 3,
  emptyLabel = 'None',
  emptyVariant = 'muted',
}: RelationChipsProps) {
  const [expanded, setExpanded] = useState(false);

  if (chips.length === 0) {
    return emptyVariant === 'warning' ? (
      <span
        className="inline-flex items-center rounded border border-amber-300 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800"
        title="The hotel / connector / SaaS relations could not be loaded. See the browser console for the query error."
      >
        {emptyLabel}
      </span>
    ) : (
      <span className="text-sm text-gray-400 italic">{emptyLabel}</span>
    );
  }

  const visible = expanded ? chips : chips.slice(0, maxVisible);
  const hiddenCount = chips.length - visible.length;

  return (
    <div className="flex flex-wrap items-center gap-1">
      {visible.map(chip => {
        const tone = chipTone(chip.status);
        const title = [chip.label, chip.hint, chip.status].filter(Boolean).join(' · ');

        return onSelect ? (
          <button
            key={chip.key}
            type="button"
            onClick={() => onSelect(chip)}
            title={`${title} — click to open`}
            className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1 ${tone}`}
          >
            {chip.label}
          </button>
        ) : (
          <span
            key={chip.key}
            title={title}
            className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${tone}`}
          >
            {chip.label}
          </span>
        );
      })}

      {hiddenCount > 0 && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="inline-flex items-center rounded-full border border-dashed border-gray-300 px-2 py-0.5 text-xs font-medium text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1"
          title={chips.slice(maxVisible).map(chip => chip.label).join(', ')}
        >
          +{hiddenCount}
        </button>
      )}

      {expanded && chips.length > maxVisible && (
        <button
          type="button"
          onClick={() => setExpanded(false)}
          className="text-xs text-gray-400 hover:text-gray-600 underline"
        >
          less
        </button>
      )}
    </div>
  );
}
